import './public-authority.scss'

import { Formik } from 'formik'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { Container, Form, Image, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import i18n from 'src/i18n'
import { NAV_TO_PUBLIC_AUTHORITY_POLICY_DETAIL, SAGA_GUARDS } from 'src/navigation'
import { shoot } from 'src/redux/actions'
import { select_error, select_loading, select_public_authority_policy } from 'src/redux/store'
import { regex_damage_policy_number, regex_tax_code, regex_vat_number } from 'src/regex'
import { ax_get_public_authority_policy, error_t } from 'src/types'
import { ClassNames } from 'src/utils/--deprecated'
import { FailureModal, Loader, Tooltip } from 'src/widgets'
import Button from 'src/widgets/--deprecated/button/button'
import InputTxt from 'src/widgets/--deprecated/input-txt/input-txt'
import Modal, {
  modalProps_genericError,
  ModalPropsDataType,
} from 'src/widgets/--deprecated/modal/modal'
import * as Yup from 'yup'

const PublicAuthority: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const loading = useSelector(select_loading())
  const public_authority_policies = useSelector(select_public_authority_policy)
  const generic_error = useSelector(select_error([...SAGA_GUARDS.POLICY_DETAIL.public]))
  const error: error_t | undefined = useSelector(select_error())

  const [is_mobile, set_is_mobile] = useState(window.innerWidth < 1200)
  const [error_visible, set_error_visible] = useState(false)

  const [modal_info, set_modal_info] = useState({
    title: t('public_authority_policy.modal.title'),
  })

  const dismiss_error = () => {
    if (error) {
      dispatch(shoot.reduce_error())
    }
  }

  //Use effect
  useEffect(() => {
    if (error?.is_tech) set_error_visible(true)
  }, [error])

  useEffect(() => {
    window.addEventListener(
      'resize',
      () => {
        const ismobile = window.innerWidth < 1200
        if (ismobile !== is_mobile) set_is_mobile(ismobile)
      },
      false
    )
  }, [is_mobile])

  useEffect(() => {
    if (public_authority_policies) navigate(NAV_TO_PUBLIC_AUTHORITY_POLICY_DETAIL)
  }, [public_authority_policies])

  const on_click_modal_generic_error_handler = () => {
    dismiss_error()
    set_error_visible(false)
  }

  const on_click_modal_verify_deposit = () => {
    window.open(process.env.REACT_APP_VERIFICA_CAUZIONE_URL)
  }

  const modal_generic_error_props: ModalPropsDataType = {
    ...modalProps_genericError.data,
    imgType: 'Error',
    showText: true,
    title: t('public_authority_policy.modal.error_title'),
    text: t('public_authority_policy.modal.error_text'),
    showBackButton: true,
    showNextButton: true,
    backButtonText: t('public_authority_policy.detail.go_back'),
    backButtonType: 'text_059881_bold',
    nextButtonText: t('public_authority_policy.detail.verify_deposit'),
    nextButtonType: 'text_059881_bold',
    closeButtonOnClick: on_click_modal_generic_error_handler,
    backButtonOnClick: on_click_modal_generic_error_handler,
    nextButtonOnClick: on_click_modal_verify_deposit,
  }

  //Form validation schema
  const validation_schema = Yup.object().shape(
    {
      policyNumber: Yup.string()
        .required(t('public_authority_policy.errors.policy_number_required'))
        .matches(
          regex_damage_policy_number,
          t('public_authority_policy.errors.policy_number_invalid')
        ),
      controlCode: Yup.string().required(t('public_authority_policy.errors.control_code_required'))
      .matches(/^[0-9]+$/, t('public_authority_policy.errors.control_code_invalid')),
      taxCode: Yup.string().when(['vatNumber'], {
        is: (vatNumber: string) => !vatNumber,
        then: Yup.string()
          .required()
          .matches(regex_tax_code, t('public_authority_policy.errors.tax_code_invalid')),
      }),
      vatNumber: Yup.string().when(['taxCode'], {
        is: (taxCode: string) => !taxCode,
        then: Yup.string()
          .required()
          .matches(regex_vat_number, t('public_authority_policy.errors.vat_number_invalid')),
      }),
    },
    [['taxCode', 'vatNumber']]
  )

  return (
    <Container fluid>
      <FailureModal error={generic_error} />
      {loading ? (
        <div className="login-loader">
          <Loader />{' '}
        </div>
      ) : (
        <div>
          <Row>
            <div className="public-authority-button-back" />
          </Row>

          <Row className="public-authority-container">
            <div>
              <div
                className={ClassNames(
                  'public-authority-title',
                  is_mobile || i18n.language === 'de' ? 'unnamed-character-style-30' : 'unnamed-character-style-29'
                )}
              >
                {t('public_authority_policy.title')}
              </div>
              <div
                className={ClassNames(
                  'public-authority-sutitle',
                  is_mobile ? 'unnamed-character-style-15-26-7f7f7f' : 'unnamed-character-style-18'
                )}
              >
                {t('public_authority_policy.subtitle')}
              </div>

              <Formik
                key="policy"
                validateOnMount={true}
                validationSchema={validation_schema}
                initialValues={{
                  policyNumber: '',
                  controlCode: '',
                  taxCode: '',
                  vatNumber: '',
                }}
                onSubmit={(values) => {
                  const params: ax_get_public_authority_policy = {
                    policyId: values.policyNumber,
                    orderNumber: values.controlCode,
                    taxCode: values.taxCode,
                    vatNumber: values.vatNumber,
                  }
                  dispatch(shoot.saga_resolve_public_authority_policy(params))
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  isValid,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="public-authority-input-container" controlId="policy">
                      <Form.Label
                        className={ClassNames(
                          'public-authority-input-label',
                          'public-authority-input-label--actual-password',
                          is_mobile
                            ? 'unnamed-character-style-27-13-22'
                            : 'unnamed-character-style-48'
                        )}
                      >
                        {t('public_authority_policy.forms.policyNumber')}
                      </Form.Label>

                      <InputTxt
                        id="policyNumber"
                        type="text"
                        value={values.policyNumber}
                        on_change={(e: ChangeEvent<HTMLInputElement>) => {
                          handleChange(e)
                          handleBlur(e)
                          dismiss_error()
                          setFieldValue('policyNumber', e.target.value.toUpperCase())
                        }}
                        validators={[
                          {
                            is_valid: () =>
                              !(
                                (touched.policyNumber && errors.policyNumber) ||
                                values.policyNumber === ''
                              ),
                            text: errors.policyNumber || '',
                          },
                        ]}
                      />
                    </Form.Group>

                    <Form.Group
                      className="public-authority-input-container"
                      controlId="controlCode"
                    >
                      <div className="public-authority-input-icon-container">
                        <Form.Label
                          className={ClassNames(
                            'public-authority-input-label',
                            is_mobile
                              ? 'unnamed-character-style-27-13-22'
                              : 'unnamed-character-style-48'
                          )}
                        >
                          {t('public_authority_policy.forms.controlCode')}
                        </Form.Label>
                        <Tooltip placement="right" width_max={180}>
                          <div>
                            <div
                              className="text-left mb-2 c-7f7f7f font-14-semibold"
                              style={{ lineHeight: 'normal' }}
                            >
                              {modal_info.title}
                            </div>
                          </div>
                        </Tooltip>
                      </div>

                      <InputTxt
                        id="controlCode"
                        type="text"
                        value={values.controlCode}
                        on_change={(e: ChangeEvent<HTMLInputElement>) => {
                          handleChange(e)
                          handleBlur(e)
                          dismiss_error()
                          setFieldValue('controlCode', e.target.value.toUpperCase())
                        }}
                        validators={[
                          {
                            is_valid: () =>
                              !(
                                (touched.controlCode && errors.controlCode) ||
                                values.controlCode === ''
                              ),
                            text: errors.controlCode || '',
                          },
                        ]}
                      />
                      {error && !error.is_tech && !(touched.controlCode && errors.controlCode) && (
                        <div className="error-character">{error?.description_fe}</div>
                      )}
                    </Form.Group>

                    <Form.Group controlId="taxCode">
                      <Form.Label
                        className={ClassNames(
                          'public-authority-input-label',
                          'public-authority-input-label--confirm-password',
                          is_mobile
                            ? 'unnamed-character-style-27-13-22'
                            : 'unnamed-character-style-48'
                        )}
                      >
                        {t('public_authority_policy.forms.taxCode')}
                      </Form.Label>

                      <InputTxt
                        id="taxCode"
                        type="text"
                        value={values.taxCode}
                        on_change={(e: ChangeEvent<HTMLInputElement>) => {
                          handleChange(e)
                          handleBlur(e)
                          dismiss_error()
                          setFieldValue('taxCode', e.target.value.toUpperCase())
                        }}
                        validators={[
                          {
                            is_valid: () =>
                              !((touched.taxCode && errors.taxCode) || values.taxCode === ''),
                            text: errors.taxCode || '',
                          },
                        ]}
                      />
                    </Form.Group>

                    <Form.Group controlId="vatNumber">
                      <Form.Label
                        className={ClassNames(
                          'public-authority-input-label',
                          'public-authority-input-label--confirm-password',
                          is_mobile
                            ? 'unnamed-character-style-27-13-22'
                            : 'unnamed-character-style-48'
                        )}
                      >
                        {t('public_authority_policy.forms.vatNumber')}
                      </Form.Label>

                      <InputTxt
                        id="vatNumber"
                        type="text"
                        value={values.vatNumber}
                        on_change={(e: ChangeEvent<HTMLInputElement>) => {
                          handleChange(e)
                          handleBlur(e)
                          dismiss_error()
                          setFieldValue('vatNumber', e.target.value.toUpperCase())
                        }}
                        validators={[
                          {
                            is_valid: () =>
                              !((touched.vatNumber && errors.vatNumber) || values.vatNumber === ''),
                            text: errors.vatNumber || '',
                          },
                        ]}
                      />
                    </Form.Group>

                    <div className="public-authority-button-submit">
                      <Button
                        justify_content="center"
                        name={is_mobile ? 'full_059881_mobile' : 'full_059881'}
                        disabled={!isValid}
                      >
                        <div>{t('public_authority_policy.forms.btn_label')}</div>
                        <div className="right-arrow-white" />
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Row>
        </div>
      )}
      {generic_error ? <Modal data={modal_generic_error_props} /> : ''}
    </Container>
  )
}

export default PublicAuthority
